<template>
  <div class="path-rule" dir="rtl">
    <div v-if="!hideTarget" class="d-flex flex-wrap align-items-center justify-content-between mb-2">
      <div class="d-flex align-items-center">
        <span class="rule-if ml-2">اگر</span>
        <button type="button" class="btn btn-sm btn-link p-0" @click="toggleMode">
          {{ ruleMode === 'any' ? 'یکی از این‌ها کافی است' : 'همه این‌ها برقرار باشند' }}
        </button>
      </div>
      <div v-if="!hideTarget">
        <button type="button" class="btn btn-sm btn-success ml-1" @click="$emit('done')">تمام</button>
        <button type="button" class="btn btn-sm btn-outline-danger" @click="$emit('remove')">حذف مسیر</button>
      </div>
    </div>

    <div v-for="(condition, index) in conditions" :key="index" class="condition-line mb-2">
      <span v-if="index > 0" class="joiner">{{ ruleMode === 'any' ? 'یا' : 'و' }}</span>
      <select class="form-control form-control-sm condition-question" :value="condition.question_id" @change="onQuestionChange(condition, $event.target.value)">
        <option value="">کدام سؤال؟</option>
        <optgroup v-if="stepQuestions.length" label="سؤال‌های همین مرحله">
          <option v-for="question in stepQuestions" :key="`s-${question.id}`" :value="question.id">
            {{ questionOptionLabel(question, false) }}
          </option>
        </optgroup>
        <optgroup v-for="group in otherQuestionGroups" :key="`g-${group.key}`" :label="group.title">
          <option v-for="question in group.questions" :key="`o-${question.id}`" :value="question.id">
            {{ questionOptionLabel(question, false) }}
          </option>
        </optgroup>
      </select>
      <select class="form-control form-control-sm condition-op" v-model="condition.op" @change="onOpChange(condition)">
        <option v-for="op in operators" :key="op.value" :value="op.value">{{ op.text }}</option>
      </select>
      <div class="condition-value">
        <div v-if="condition.op === 'between'" class="d-flex">
          <input type="number" class="form-control form-control-sm ml-1" :value="betweenValue(condition, 0)"
                 @input="setBetweenValue(condition, 0, $event.target.value)" placeholder="از">
          <input type="number" class="form-control form-control-sm" :value="betweenValue(condition, 1)"
                 @input="setBetweenValue(condition, 1, $event.target.value)" placeholder="تا">
        </div>
        <select
          v-else-if="questionOptions(condition.question_id).length && condition.op !== 'in' && condition.op !== 'not_in'"
          class="form-control form-control-sm"
          v-model="condition.value"
        >
          <option value="">کدام مقدار؟</option>
          <option v-for="option in questionOptions(condition.question_id)" :key="option.value" :value="option.value">
            {{ option.text }}
          </option>
        </select>
        <input
          v-else
          type="text"
          class="form-control form-control-sm"
          :value="inValue(condition)"
          @input="setScalarValue(condition, $event.target.value)"
          :placeholder="(condition.op === 'in' || condition.op === 'not_in') ? 'مثلا 18, 19, 20' : 'مقدار'"
        >
      </div>
      <button type="button" class="btn btn-sm btn-light remove-cond" @click="removeCondition(index)" title="حذف">×</button>
    </div>

    <button type="button" class="btn btn-sm btn-light border mb-3" @click="addCondition">
      {{ ruleMode === 'any' ? '+ یا این هم' : '+ و این هم' }}
    </button>

    <div v-if="!hideTarget" class="d-flex flex-wrap align-items-center">
      <span class="goto-label ml-2">برو به</span>
      <select class="form-control form-control-sm goto-select" v-model="rule.next">
        <option value="">مرحله بعدی در لیست</option>
        <option value="end">پایان فرم</option>
        <option v-for="item in otherSteps" :key="item.id" :value="item.id">
          {{ item.title || item.id }}
        </option>
      </select>
    </div>
  </div>
</template>

<script>
import {bindConditionQuestion, groupQuestionsByStep, questionRefLabel} from "../formQuestionContext";

const OPERATORS = [
  {value: 'eq', text: 'برابر باشد با'},
  {value: 'neq', text: 'برابر نباشد با'},
  {value: 'gt', text: 'بزرگتر باشد از'},
  {value: 'gte', text: 'حداقل'},
  {value: 'lt', text: 'کوچکتر باشد از'},
  {value: 'lte', text: 'حداکثر'},
  {value: 'between', text: 'بین'},
  {value: 'in', text: 'یکی از این‌ها باشد'},
  {value: 'not_in', text: 'هیچ‌کدام از این‌ها نباشد'},
  {value: 'contains', text: 'شامل باشد'}
]

export default {
  name: "FormPathRuleEditor",
  props: {
    rule: {
      type: Object,
      required: true
    },
    stepQuestions: {
      type: Array,
      default: () => []
    },
    otherQuestions: {
      type: Array,
      default: () => []
    },
    otherSteps: {
      type: Array,
      default: () => []
    },
    allQuestions: {
      type: Array,
      default: () => []
    },
    hideTarget: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {operators: OPERATORS}
  },
  computed: {
    ruleMode() {
      return this.rule.when && this.rule.when.any ? 'any' : 'all'
    },
    conditions() {
      if (this.rule.when && Array.isArray(this.rule.when.all)) {
        return this.rule.when.all
      }
      if (this.rule.when && Array.isArray(this.rule.when.any)) {
        return this.rule.when.any
      }
      return []
    },
    contextQuestions() {
      if (this.allQuestions.length) {
        return this.allQuestions
      }
      return this.stepQuestions.concat(this.otherQuestions)
    },
    otherQuestionGroups() {
      return groupQuestionsByStep(this.contextQuestions, this.otherQuestions)
    }
  },
  methods: {
    questionById(id) {
      return this.contextQuestions.find(item => String(item.id) === String(id))
    },
    questionOptionLabel(question, withStep) {
      return questionRefLabel(this.contextQuestions, question, {withStep})
    },
    onQuestionChange(condition, questionId) {
      bindConditionQuestion(this.contextQuestions, condition, questionId)
      this.$set(condition, 'question_id', questionId)
      this.$set(condition, 'step_id', condition.step_id)
      this.$set(condition, 'variable', condition.variable)
    },
    questionOptions(questionId) {
      const question = this.questionById(questionId)
      if (!question || !Array.isArray(question.options)) {
        return []
      }
      return question.options.map(option => ({
        value: option.id,
        text: option.label || option.id
      }))
    },
    emptyCondition() {
      return {question_id: '', step_id: '', variable: '', op: 'eq', value: ''}
    },
    toggleMode() {
      const next = this.ruleMode === 'any' ? 'all' : 'any'
      const conditions = this.conditions.slice()
      this.$set(this.rule, 'when', next === 'any' ? {any: conditions} : {all: conditions})
    },
    addCondition() {
      if (!this.conditions.length) {
        this.$set(this.rule, 'when', {all: [this.emptyCondition()]})
        return
      }
      this.conditions.push(this.emptyCondition())
    },
    removeCondition(index) {
      this.conditions.splice(index, 1)
    },
    onOpChange(condition) {
      if (condition.op === 'between') {
        const current = Array.isArray(condition.value) ? condition.value : [condition.value, '']
        this.$set(condition, 'value', [current[0] || '', current[1] || ''])
        return
      }
      if (Array.isArray(condition.value) && condition.op !== 'in') {
        this.$set(condition, 'value', condition.value[0] !== undefined ? condition.value[0] : '')
      }
    },
    betweenValue(condition, index) {
      return Array.isArray(condition.value) ? condition.value[index] : ''
    },
    setBetweenValue(condition, index, raw) {
      const next = Array.isArray(condition.value) ? condition.value.slice() : ['', '']
      next[index] = this.castValue(raw)
      this.$set(condition, 'value', next)
    },
    inValue(condition) {
      if ((condition.op === 'in' || condition.op === 'not_in') && Array.isArray(condition.value)) {
        return condition.value.join(', ')
      }
      return condition.value === undefined || condition.value === null ? '' : condition.value
    },
    setScalarValue(condition, raw) {
      if (condition.op === 'in' || condition.op === 'not_in') {
        const values = String(raw)
          .split(',')
          .map(item => item.trim())
          .filter(item => item !== '')
          .map(item => this.castValue(item))
        this.$set(condition, 'value', values)
        return
      }
      this.$set(condition, 'value', this.castValue(raw))
    },
    castValue(raw) {
      if (raw === '' || raw === null || typeof raw === 'undefined') {
        return raw
      }
      const asNumber = Number(raw)
      if (!isNaN(asNumber) && String(raw).trim() !== '' && String(asNumber) === String(raw).trim()) {
        return asNumber
      }
      return raw
    }
  }
}
</script>

<style scoped>
.path-rule {
  background: #fbfafe;
  border: 1px solid #ece6f7;
  border-radius: 10px;
  padding: 12px;
}

.rule-if,
.goto-label,
.joiner {
  font-weight: 700;
  color: #6f42c1;
  min-width: 28px;
}

.condition-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.condition-question {
  flex: 1 1 180px;
}

.condition-op {
  flex: 0 1 160px;
}

.condition-value {
  flex: 1 1 160px;
}

.goto-select {
  max-width: 280px;
}

.remove-cond {
  line-height: 1;
  width: 32px;
}
</style>
